// Frontend view of the caller's effective permissions. The worker resolves
// role defaults + agency role overrides + personal exceptions per request;
// the client fetches that resolved map once and gates UI with it. Server
// enforcement is the boundary — this is visibility only.
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/state/auth";
import { ROLE_LABEL, type BuiltinRole } from "@shared/permissionSchema";

export type PermsSelf = { role: string; perms: Record<string, boolean> };

/** Human label for a builtin, legacy, or custom role slug. */
export function roleDisplayLabel(role: string | null | undefined): string {
  if (!role) return "";
  if (role === "member") return "Member (legacy)";
  if (role in ROLE_LABEL) return ROLE_LABEL[role as BuiltinRole];
  // Custom role slugs → Title Case words
  return role
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") || role;
}

/** Roles the agency-member API still ranks as management (not perm-grid). */
export function isAgencyManagerRole(role: string | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

export function usePerm(): {
  can: (key: string) => boolean;
  ready: boolean;
  role: string | undefined;
  isOwner: boolean;
} {
  const { activeAgency } = useAuth();
  const isOwner = activeAgency?.role === "owner";
  const q = useQuery<PermsSelf>({
    queryKey: ["/api/permissions/self", activeAgency?.id],
    queryFn: () => api.get<PermsSelf>("/api/permissions/self"),
    enabled: !!activeAgency && !isOwner, // owners are allow-all, skip the fetch
    staleTime: 60_000,
  });
  const perms = q.data?.perms;
  return {
    can: (key: string) => (isOwner ? true : perms?.[key] === true),
    ready: isOwner || !!perms,
    // Prefer the role the permissions endpoint echoes (same as activeAgency,
    // but keeps one source of truth once /self has loaded).
    role: q.data?.role ?? activeAgency?.role,
    isOwner,
  };
}
